// Copyright (c) 2026, Cyber Cube and contributors
// For license information, please see license.txt
//
// The camera scanner — `PHASE-9.md` §8.5's `⬤ Scan`, and the `/inspect/scan`
// screen.
//
// ## The library, and why it is not in a bundle
//
// `html5-qrcode` **2.3.8**, Apache-2.0, vendored at
// `qhse/public/vendor/html5-qrcode/` with its `LICENSE` and a `VENDOR.md` carrying
// the sha256 (Q3/D1 — vendored as source, never a runtime dependency, and
// `required_apps` stays `[]`). Adopted under the owner's decision of 2026-09-12,
// which widened `PHASE-9.md` §7's MIT/BSD rule because the licence-free
// alternative — the native `BarcodeDetector` alone — has **no camera path on iOS
// Safari**, and that is a functional hole in a field tool rather than a purity
// win.
//
// It is **375 KB**, so it is not in `web_include_js`, not in `app_include_js` and
// not imported by any bundle. `load()` injects a `<script>` the first time a scan
// actually starts and caches the promise. A field inspector opening `/inspect` to
// read a list must not download a decoder they never use, on a phone, on site.
//
// ## The camera's absence is explained, not silent (CONVENTIONS #2)
//
// `getUserMedia` is **absent**, not failing, outside a secure context:
// `navigator.mediaDevices` is `undefined` on plain HTTP, so the idiomatic
// `try`/`catch` catches nothing and a scanner button is simply inert. Two halves
// of one answer:
//
//   * the **client** checks `window.isSecureContext` and `navigator.mediaDevices`,
//     which is the authority for *this* tab;
//   * the **server** supplies the *explanation* through M9.8's
//     `facility_qr.camera_capability(get_url())`, and it earns its keep on the one
//     case the client cannot see — `http://localhost` **is** a secure context and
//     `http://192.168.1.5` is not, which is the "works on my laptop, dead on the
//     phone" deployment trap and is server-knowable.
//
// Either way the screen offers **typing the tag** instead. A scanner with no
// camera is a screen with a text box, never a dead button.
//
// ## A code that resolves to nothing says so
//
// Resolution is M9.8's `resolve_tag`, which accepts the printed route
// `/app/qhse-facility-asset/<tag>`, this module's `/app/facility/asset/<tag>` and a
// bare tag, and **throws naming the code** when none of them resolves — never an
// empty success, because a scan screen that silently returns nothing looks
// identical to a camera that did not fire (ARCH §6.24). An asset that resolves but
// is **not in this round** is the caller's `onResolved` to report; `capture.js`
// says so by name.

frappe.provide("qhse.facility");

const R = () => qhse.facility.render;
const NET = () => qhse.facility.net;

//: The vendored bundle's URL under `sites/assets`. Everything below
//: `qhse/public` is symlinked to `/assets/qhse` by `bench build`, so this path is
//: the same on every site and needs no `frappe.boot` lookup.
//:
//: Note the wording: a `//` comment must never contain a block-comment opener.
//: Every source-reading check in this repo strips block comments FIRST, so a
//: glob written with a star-slash-star inside a line comment swallows the file
//: down to the next close marker — the checks then read a truncated file and an
//: `assertNotIn` passes for the wrong reason. Found at M9.9c; CLAUDE.md §15.20,
//: and `test_facility_page.py` now fails on it rather than leaving it to be
//: rediscovered.
const LIBRARY_URL = "/assets/qhse/vendor/html5-qrcode/html5-qrcode.min.js";

//: The UMD global the bundle publishes. Named as a constant so the absence check
//: below and the load check cannot disagree about what "loaded" means.
const LIBRARY_GLOBAL = "__Html5QrcodeLibrary__";

//: Every `open()` mounts on a FRESH element id (M9.25). `html5-qrcode` resolves
//: its mount with `document.getElementById`, so two overlays that both used the
//: constant id — the one closing and the one opening — are a coin toss over
//: which element the new scanner attaches to, and the loser is a camera that
//: never appears. A counter costs nothing and removes the race entirely.
let viewportSeq = 0;

//: The element the decoder mounts into. `html5-qrcode`'s constructor takes an
//: element **id**, so this one needs to exist in the document by the time the
//: scanner starts — which is why the modal is shown before `start()` is called.
const VIEWPORT_ID = "qhse-fac-scan-viewport";

//: How long to wait for that node to reach the document, **in milliseconds**.
//:
//: M9.31, and the unit is the whole fix. `frappe.get_modal` returns a DETACHED
//: wrapper; bootstrap appends it to `<body>` inside `_showElement`, which runs
//: only after the backdrop's 150 ms CSS transition has ended (ARCH §10.52). So
//: the thing being waited for is a duration.
//:
//: M9.25 budgeted 20 animation FRAMES instead, which measures the DISPLAY: 333
//: ms at 60 Hz, 167 ms at 120 Hz, 139 ms at 144 Hz. Below 150 ms the budget
//: expires first, `_start` runs anyway and the library throws its bare string
//: `HTML Element with id=… not found` — the owner's report, word for word.
//: Measured at three simulated refresh rates before this change: 60 Hz mounted
//: on frame 10 of 20 and always worked; 120 Hz exhausted all 20 and worked by a
//: hair; 144 Hz exhausted them at 122 ms and failed every time. A bigger frame
//: count only moves the cliff — a clock has no cliff.
const MOUNT_TIMEOUT_MS = 6000;

//: Poll interval for that wait. A timer, not `requestAnimationFrame`, for the
//: same reason: rAF ties the poll to the refresh rate of the screen.
const MOUNT_POLL_MS = 20;

let loading = null;

qhse.facility.scan = {
	LIBRARY_URL,
	VIEWPORT_ID,

	/** Can this tab open a camera at all? The client's half of CONVENTIONS #2.
	 *
	 * Reads two things and neither is a `try`/`catch`: outside a secure context
	 * `navigator.mediaDevices` is `undefined`, so there is no call to wrap and no
	 * exception to catch. `reason` is the server's sentence when the caller has one
	 * (the scan page passes `camera_capability`'s), because the server knows things
	 * this tab does not.
	 */
	capability(serverReason) {
		const secure = typeof window !== "undefined" && window.isSecureContext !== false;
		const media = !!(
			typeof navigator !== "undefined" &&
			navigator.mediaDevices &&
			navigator.mediaDevices.getUserMedia
		);
		if (secure && media) return { available: true, reason: "" };
		return {
			available: false,
			reason:
				serverReason ||
				(!secure
					? __("The camera needs a secure connection (https). Type the asset tag instead.")
					: __("This browser offers no camera to this page. Type the asset tag instead.")),
		};
	},

	/** Inject the vendored decoder once, and resolve with its namespace.
	 *
	 * The promise is cached, so a second scan re-uses the first load; a **failed**
	 * load clears the cache so a retry is a real retry rather than a replay of the
	 * failure. Absence is reported by name — an inert scan button has a plausible
	 * innocent explanation, which is exactly why it must not be the failure mode
	 * (D11's rule).
	 */
	load() {
		if (window[LIBRARY_GLOBAL]) return Promise.resolve(window[LIBRARY_GLOBAL]);
		if (loading) return loading;

		loading = new Promise((resolve, reject) => {
			const script = document.createElement("script");
			script.src = LIBRARY_URL;
			script.async = true;
			script.addEventListener("load", () => {
				if (window[LIBRARY_GLOBAL]) resolve(window[LIBRARY_GLOBAL]);
				else {
					reject(
						new Error(
							__("The scanner library loaded but published nothing at {0}.", [
								LIBRARY_GLOBAL,
							])
						)
					);
				}
			});
			script.addEventListener("error", () => {
				reject(
					new Error(
						__("The scanner library could not be loaded from {0}.", [LIBRARY_URL])
					)
				);
			});
			document.head.appendChild(script);
		}).catch((error) => {
			loading = null;
			throw error;
		});
		return loading;
	},

	/** Open the scanner.
	 *
	 * `options.round` is the round a hit should jump within; `options.onResolved`
	 * is handed `{asset, tag}` and returns truthy when it handled it. With no
	 * handler, a resolved code opens the asset's own page — which is what
	 * `/inspect/scan` does when it was not opened from a round.
	 */
	open(options) {
		const opts = options || {};
		const capability = this.capability(opts.cameraReason);

		// Tear down anything still running from a previous open BEFORE building
		// the new overlay, so `_start` below has a teardown to wait on rather
		// than a camera to fight (M9.25).
		this._stop();

		const modal = R().dialog(__("Scan an asset"), __("Close"), (dialog) => {
			this._stop(modal.pass);
			dialog.hide();
		});
		modal.setPrimary(true, "");

		// **Which camera pass this overlay owns.** Every teardown below names
		// it, and `_stop` refuses to tear down a pass that is no longer the one
		// running (M9.31). Without that, the handler below — which bootstrap
		// fires ~300 ms after the close, when the *next* scan can already have a
		// camera — stopped the scanner the next overlay had just started:
		// measured at 135 ms after its `start`, leaving a black rectangle under
		// the words "Point the camera at the sticker".
		modal.pass = null;

		// **Destroy the overlay when it closes.** `frappe.ui.Dialog` hides; it
		// does not remove. Every scan therefore left a hidden modal in the
		// document, each holding a dead viewport — found because a probe's
		// `querySelector(".qhse-fac-scan-viewport")` kept matching the FIRST
		// scan's node three opens later. Over a shift of scanning that is a
		// growing pile, and it is also what makes "is the scanner mounted?"
		// ambiguous for anything that asks. Removal waits for
		// `hidden.bs.modal` so bootstrap finishes its own teardown first and no
		// backdrop is stranded.
		if (modal.dialog && modal.dialog.$wrapper) {
			modal.dialog.$wrapper.on("hidden.bs.modal", () => {
				// **Wait for the teardown before removing the node.** Removing it
				// synchronously is what kept the camera held: `stop()` releases
				// the MediaStream through the `<video>` it owns, and that video
				// goes with the wrapper. Reproduced with a stub decoder —
				// `STOP-TOO-LATE(node gone, camera still held)` followed by
				// `START-REJECTED(camera busy)` on the next open, which is the
				// "camera could not be started" the owner saw on every scan after
				// the first.
				this._stop(modal.pass).then(() => modal.dialog.$wrapper.remove());
			});
		}

		const body = modal.body;
		const status = R().el("p", { class: "qhse-fac-scan-status" }, "");

		// Always present, whether or not a camera is. §8.5's contract is that the
		// scanner reaches the right asset — the camera is the fast path, not the
		// only one.
		const manual = R().input(
			__("Or type the asset tag"),
			"text",
			"",
			() => {},
			{ placeholder: __("FE-105") }
		);
		const go = R().el(
			"button",
			{ type: "button", class: "qhse-fac-button is-primary" },
			__("Open")
		);
		go.addEventListener("click", () => {
			const typed = manual.control.value.trim();
			if (!typed) {
				status.textContent = __("Type a tag, or scan one.");
				return;
			}
			this._resolve(typed, opts, modal, status);
		});
		manual.control.addEventListener("keydown", (event) => {
			if (event.key === "Enter") go.click();
		});

		// The camera view is a FUNCTION, not a one-off paint, because M9.25 gives
		// the overlay a second state — the confirmation card — and `[ Scan again ]`
		// has to be able to come back here. Each pass mounts on a fresh element
		// id (`viewportSeq`), which is what makes re-entry safe.
		const showCamera = () => {
			R().clear(body);
			if (!capability.available) {
				// Hidden, not greyed out, and the reason is on screen as a sentence —
				// CONVENTIONS #2: an unavailable capability is invisible with a small
				// explanation, never a dead control.
				status.textContent = capability.reason;
				status.className = "qhse-fac-scan-status is-warn";
				R().append(body, status, manual, go);
				manual.control.focus();
				return;
			}
			viewportSeq += 1;
			const viewportId = `${VIEWPORT_ID}-${viewportSeq}`;
			// The pass is claimed HERE, synchronously, because everything that
			// follows is asynchronous and each step has to be able to ask "am I
			// still the current attempt?". `[ Scan again ]` re-enters this
			// function, so a pending `_start` from the previous pass must find
			// that it has been superseded and do nothing.
			const passId = viewportSeq;
			modal.pass = passId;
			this._passId = passId;
			const viewport = R().el("div", {
				class: "qhse-fac-scan-viewport",
				id: viewportId,
			});
			status.className = "qhse-fac-scan-status";
			status.textContent = __("Loading the scanner…");
			R().append(body, viewport, status, manual, go);
			// **Wait for the node to actually be in the document before starting.**
			// `html5-qrcode` resolves its mount with `getElementById`, and the
			// dialog's body is not in the document yet at microtask time: frappe
			// builds a detached wrapper and bootstrap appends it only when the
			// backdrop transition ends (ARCH §10.52). With the real library a
			// start against a missing node is `HTML Element with id=… not found`.
			//
			// This is §15.7's rule ("a control has no value until it is
			// rendered") reached through a third-party library, and it is NOT the
			// geometry read §10.31 forbids — presence is not size, and nothing
			// here scales to a measurement.
			this._whenMounted(viewportId).then(() => {
				if (this._passId !== passId) return;
				this._start(status, opts, modal, viewportId, passId);
			});
		};
		modal.showCamera = showCamera;

		modal.dialog.show();
		showCamera();
		return modal;
	},

	/** The confirmation card — M9.25, and the owner asked for it by name.
	 *
	 * A scan used to jump straight to the asset, so a misread sticker took the
	 * inspector somewhere else with nothing to check against. Now the overlay
	 * says **which asset it thinks it read**, and the inspector either takes it
	 * or scans again. The camera is already stopped when this renders — one hit
	 * is enough — so `[ Scan again ]` is a real restart through `showCamera()`,
	 * which mounts a fresh viewport id and waits on the teardown.
	 *
	 * The card is filled from `asset_summary`, which is the endpoint built for
	 * exactly this ("who, what, where, and when last inspected"). If it fails,
	 * the tag alone is still shown and still actionable — a confirmation step
	 * that can refuse to confirm would be worse than the jump it replaced.
	 */
	_confirm(resolved, opts, modal, status) {
		const tag = resolved.tag || resolved.asset;
		const body = modal.body;
		R().clear(body);

		const card = R().el("div", { class: "qhse-fac-scan-card" });
		card.appendChild(R().el("p", { class: "qhse-fac-scan-found" }, __("Scanned")));
		card.appendChild(R().el("p", { class: "qhse-fac-scan-tag" }, tag));
		const facts = R().el("div", { class: "qhse-fac-scan-facts" });
		card.appendChild(facts);
		body.appendChild(card);

		const actions = R().el("div", { class: "qhse-fac-scan-actions" });
		const open = R().el(
			"button",
			{ type: "button", class: "qhse-fac-button is-primary qhse-fac-scan-open" },
			opts.onResolved ? __("Go to this asset") : __("Open this asset")
		);
		const again = R().el(
			"button",
			{ type: "button", class: "qhse-fac-button qhse-fac-scan-again" },
			__("Scan again")
		);
		again.addEventListener("click", () => modal.showCamera());
		open.addEventListener("click", () => this._take(resolved, opts, modal, status));
		R().append(actions, open, again);
		body.appendChild(actions);
		body.appendChild(status);
		status.className = "qhse-fac-scan-status";
		status.textContent = "";

		// Detail is an enhancement on top of a card that already works.
		NET()
			.call("qhse.api.facility_assets.asset_summary", { code: tag })
			.then((summary) => {
				const row = (summary && summary.fields) || {};
				R().clear(facts);
				[
					[__("Asset"), row.asset_name],
					[__("Type"), row.asset_type],
					[__("Where"), row.zone_path],
					[__("Status"), row.status],
					[__("Last verdict"), row.last_inspection_verdict],
				].forEach(([label, value]) => {
					if (!value) return;
					const line = R().el("div", { class: "qhse-fac-scan-fact" });
					R().append(
						line,
						R().el("span", { class: "qhse-fac-scan-fact-label" }, label),
						// `.text()`, never markup — every one of these is
						// operator-typed (§10.24).
						R().el("span", { class: "qhse-fac-scan-fact-value" }, String(value))
					);
					facts.appendChild(line);
				});
			})
			.catch(() => {
				/* The tag is on screen and the buttons work; detail is a bonus. */
			});
		return modal;
	},

	/** Take the confirmed asset — the half `_resolve` used to do on its own. */
	_take(resolved, opts, modal, status) {
		const close = () => {
			// Named, like every other teardown in this module (M9.31): an
			// unnamed stop means "stop whatever is running", and this one runs
			// while an overlay is closing.
			if (modal) this._stop(modal.pass);
			if (modal && modal.dialog) modal.dialog.hide();
		};
		if (opts.onResolved) {
			if (opts.onResolved(resolved)) {
				close();
				return resolved;
			}
			status.className = "qhse-fac-scan-status is-warn";
			status.textContent = __("{0} is not an asset in this round.", [
				resolved.tag || resolved.asset,
			]);
			return resolved;
		}
		close();
		this.openAsset(resolved.tag || resolved.asset);
		return resolved;
	},

	/** Resolve once `id` is in the document, or when the deadline passes.
	 *
	 * Bounded rather than open-ended: a poll that could wait forever turns a
	 * missing node into a spinner with no end — the failure mode D11 exists to
	 * prevent. It resolves `true` when the node arrived and `false` when the
	 * deadline did, so the caller can say which happened instead of handing the
	 * library a node it will not find.
	 *
	 * **The bound is a clock, not a frame count** — see `MOUNT_TIMEOUT_MS`. The
	 * M9.25 version counted 20 `requestAnimationFrame` callbacks, which is 333
	 * ms on a 60 Hz laptop and 139 ms on a 144 Hz phone; the transition it was
	 * waiting for is 150 ms on both. That is the whole of the owner's "works on
	 * the first click, then the camera could not be started" report, and it is
	 * why the faster the device, the more certainly it failed.
	 */
	_whenMounted(id, deadline) {
		const until = deadline === undefined ? Date.now() + MOUNT_TIMEOUT_MS : deadline;
		if (document.getElementById(id)) return Promise.resolve(true);
		if (Date.now() >= until) return Promise.resolve(false);
		return new Promise((resolve) => {
			window.setTimeout(() => resolve(this._whenMounted(id, until)), MOUNT_POLL_MS);
		});
	},

	_start(status, opts, modal, viewportId, passId) {
		// One hit is enough — and `_stop()` is asynchronous, so the decoder keeps
		// delivering frames while it winds down. Without this latch one sticker
		// fired `_resolve` two or three times: two lookups, and a second
		// `_confirm` that clears the overlay body — possibly under a
		// `[ Scan again ]` that had already mounted a new viewport into it.
		let handled = false;

		// Wait for any previous camera to be fully released before asking for it
		// again (M9.25). Without this the second `open()` races the first one's
		// teardown, and the loser is always the camera.
		(this._stopping || Promise.resolve())
			.then(() => this.load())
			.then((library) => {
				// Superseded while we were waiting — the overlay closed, or
				// `[ Scan again ]` mounted a newer viewport. Opening a camera
				// now would be one nobody can see and nobody will stop.
				if (this._passId !== passId) return null;
				const Scanner = library.Html5Qrcode;
				if (!Scanner) {
					throw new Error(__("The scanner library published no decoder."));
				}
				if (!document.getElementById(viewportId)) {
					// The library's own answer here is a bare string naming an
					// element id, which is the message the owner had to report
					// back to us. Ours names what the inspector can DO.
					throw new Error(
						__(
							"The scanner view did not open in time. Close this and scan again, or type the asset tag instead."
						)
					);
				}
				this._scanner = new Scanner(viewportId, {
					// The native decoder when the browser has one — it is faster and
					// cheaper — with the vendored ZXing as the fallback, which is the
					// whole reason this library was chosen over `BarcodeDetector`
					// alone (no camera path on iOS Safari).
					useBarCodeDetectorIfSupported: true,
					verbose: false,
				});
				status.textContent = __("Point the camera at the sticker.");
				this._mountId = viewportId;
				return this._scanner.start(
					{ facingMode: "environment" },
					{ fps: 10, qrbox: 240 },
					(decoded) => {
						// One hit is enough, and the latch is what makes that
						// true: `_stop()` does not take effect until the
						// library has wound the camera down, and every frame
						// until then decodes the same sticker again.
						if (handled || this._passId !== passId) return;
						handled = true;
						this._stop(passId);
						this._resolve(decoded, opts, modal, status);
					},
					() => {
						/* per-frame "no code in this frame" — not an error */
					}
				).then(() => {
					// Keep our own handle on the MediaStream. The library
					// releases it through the `<video>` it mounted, so if that
					// element is gone by the time `stop()` runs, the tracks stay
					// live and the next `getUserMedia` fails with "could not
					// start video source". Holding the stream means the release
					// below cannot depend on the DOM at all.
					const host = document.getElementById(viewportId);
					const video = host && host.querySelector("video");
					this._stream = (video && video.srcObject) || null;
				});
			})
			.catch((error) => {
				// Scoped like every teardown in this module (M9.31): a failure
				// belonging to a pass that has been superseded must not release
				// the camera a NEWER pass is holding, and must not write a
				// warning onto an overlay that has moved on. `_releaseTracks`
				// ends `this._stream`, which by then belongs to someone else.
				if (this._passId !== passId) return;
				// **`html5-qrcode` rejects with a bare STRING**, not an `Error`,
				// so `error.message` is `undefined` and every camera failure
				// collapsed into one generic sentence that named nothing. That is
				// how the owner's second-scan failure arrived with no diagnosis
				// attached. A reason the user can read is also a reason they can
				// report.
				const reason =
					typeof error === "string"
						? error
						: (error && (error.message || error.name)) || "";
				this._releaseTracks();
				status.className = "qhse-fac-scan-status is-warn";
				status.textContent = reason
					? __("The camera could not be started: {0}", [String(reason)])
					: __("The camera could not be started. Type the asset tag instead.");
			});
	},

	/** Tear the camera down, and **resolve only when it is actually down**.
	 *
	 * M9.25 — the second-scan defect. This used to fire `scanner.stop()` and
	 * return immediately, so `.then(() => scanner.clear())` landed at some later
	 * tick. Open the scanner again inside that window and two things went wrong
	 * at once: the previous instance's `clear()` wiped the NEW overlay's mount
	 * node, and the camera track was still held, so `start()` either failed or
	 * attached to nothing. The symptom the owner reported is exact — the first
	 * scan works and every later one is dead.
	 *
	 * So the teardown is now a promise every caller can wait on, `_start` waits
	 * on it, and `clear()` runs on both the success and the failure path —
	 * stopping a scanner that is already stopped throws, and that must not skip
	 * the cleanup that follows it.
	 *
	 * **`passId` scopes it (M9.31).** A teardown that names a pass tears down
	 * only that pass; naming none means "stop whatever is running", which is
	 * what `open()` wants and nothing else does. The difference was measured:
	 * bootstrap fires `hidden.bs.modal` ~300 ms after the close click, so the
	 * closing overlay's unnamed `_stop()` reached the camera the NEXT overlay
	 * had started 135 ms earlier and killed it — with the status line still
	 * reading "Point the camera at the sticker", because nothing had failed.
	 *
	 * Clearing `_passId` is not bookkeeping either: it is what tells a `_start`
	 * still waiting on its mount that its pass is over, so a camera cannot open
	 * behind an overlay that is already gone.
	 */
	_stop(passId) {
		if (passId !== undefined && passId !== this._passId) {
			return this._stopping || Promise.resolve();
		}
		this._passId = null;
		const scanner = this._scanner;
		this._scanner = null;
		if (!scanner) return this._stopping || Promise.resolve();
		const settle = () => {
			// Belt and braces, and it is the half that does not trust the
			// library: whatever `stop()` did or failed to do, the tracks are
			// ours to end. A track left live is a camera the next `open()`
			// cannot have.
			this._releaseTracks();
			try {
				scanner.clear();
			} catch (error) {
				// Nothing mounted; the element is already gone with its modal.
			}
		};
		let chain;
		try {
			chain = scanner.isScanning
				? scanner.stop().then(settle, settle)
				: Promise.resolve().then(settle);
		} catch (error) {
			// `isScanning` itself throws on a half-built instance.
			chain = Promise.resolve().then(settle);
		}
		this._stopping = chain.then(() => {
			this._stopping = null;
		});
		return this._stopping;
	},

	/** End every track this module started, without touching the DOM.
	 *
	 * `_stream` is captured when `start()` resolves precisely so this can run
	 * after the overlay is gone. `getTracks().forEach(stop)` is the only thing
	 * that actually frees a camera; `MediaStreamTrack.stop()` on an already-ended
	 * track is a no-op, so calling this twice is safe and calling it late is
	 * still better than not calling it.
	 */
	_releaseTracks() {
		const stream = this._stream;
		this._stream = null;
		this._mountId = null;
		if (!stream || !stream.getTracks) return;
		stream.getTracks().forEach((track) => {
			try {
				track.stop();
			} catch (error) {
				/* already ended */
			}
		});
	},

	/** Resolve a code through `resolve_tag` and hand it to the caller.
	 *
	 * Three outcomes, three different messages — because "nothing happened" is the
	 * failure `parse_code` was built to avoid (ARCH §6.24):
	 *
	 *   1. unresolvable → the server's own throw, naming the code;
	 *   2. resolved but not in this round → the caller's `onResolved` returns
	 *      falsy and has already said so by name;
	 *   3. resolved and handled → the modal closes.
	 */
	/** Resolve a typed tag with **no overlay** — `/inspect/scan`'s in-page path.
	 *
	 * M9.16: where the browser refuses the camera, the scan screen keeps its
	 * manual entry on the page instead of inside an empty scanner overlay
	 * (`portal.js::_manualEntry`, `W1.9`/`LS6`). It is the same `_resolve` the
	 * scanner and the overlay's own text box use, so "what does this code mean"
	 * still has exactly one definition (CONVENTIONS #24) and an out-of-round
	 * asset is still refused by name.
	 */
	resolveTyped(code, options) {
		const opts = options || {};
		return this._resolve(code, opts, null, opts.status);
	},

	_resolve(code, opts, modal, status) {
		status.className = "qhse-fac-scan-status";
		status.textContent = __("Looking up {0}…", [code]);
		return NET()
			.call("qhse.api.facility_assets.resolve_tag", { code })
			.then((resolved) => {
				if (!resolved || !resolved.asset) {
					throw new Error(__("{0} is not an asset tag.", [code]));
				}
				// M9.25. With an overlay, a hit CONFIRMS before it jumps: the
				// inspector sees which asset the code resolved to and chooses.
				// Without one — `/inspect/scan`'s in-page path, where there is no
				// modal to host a card — the old behaviour is unchanged, because
				// that screen already shows the asset it found on the page.
				if (modal) {
					this._confirm(resolved, opts, modal, status);
					return resolved;
				}
				return this._take(resolved, opts, null, status);
			})
			.catch((error) => {
				status.className = "qhse-fac-scan-status is-warn";
				status.textContent =
					error && error.message ? String(error.message) : __("That code did not resolve.");
			});
	},

	/** Open one asset's page.
	 *
	 * `/app/facility/asset/<tag>` is the M9.9a page, and it is the route
	 * `parse_code` has accepted since M9.8 — so the reader that was "wider than the
	 * writer" is now true in both directions. Printed stickers still encode
	 * `/app/qhse-facility-asset/<tag>` (the owner's M9.8 decision), and no label is
	 * stranded either way.
	 */
	openAsset(tag) {
		const route = `/app/facility/asset/${encodeURIComponent(tag)}`;
		if (window.frappe && frappe.set_route) frappe.set_route(["facility", "asset", tag]);
		else window.location.href = route;
	},
};
